import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';
import {
  compareTags,
  currentLactation,
  deriveReproStatus,
  deriveStage,
  groupEventsByAnimal,
  type CurrentLactation,
  type DomainAnimal,
  type DomainEvent,
  type ReproInfo,
  type Stage,
} from '@dairy/domain';
import type { AnimalRecord, EventRecord, PullUser } from '@dairy/shared';
import { useSettings } from '@/auth/useSession';
import { farmToday } from '@/features/events/effects';
import { db, getMeta } from './db';

export function useToday(): string {
  // Recomputed on every render; cheap and keeps "today" right after midnight.
  return farmToday();
}

export function useAnimals(): AnimalRecord[] | undefined {
  return useLiveQuery(() => db.animals.toArray(), []);
}

export function useEvents(): EventRecord[] | undefined {
  return useLiveQuery(() => db.events.toArray(), []);
}

export function useAnimalEvents(animalId: string | undefined): EventRecord[] | undefined {
  return useLiveQuery(
    () => (animalId ? db.events.where('animalId').equals(animalId).toArray() : Promise.resolve([] as EventRecord[])),
    [animalId],
  );
}

export interface AnimalRow {
  animal: AnimalRecord;
  stage: Stage;
  repro: ReproInfo | null;
  lact: CurrentLactation | null;
  events: DomainEvent[];
}

export interface HerdView {
  rows: AnimalRow[];
  byId: Map<string, AnimalRow>;
  eventsByAnimal: Map<string, DomainEvent[]>;
  loading: boolean;
}

/** AR-2: stage, reproductive status and lactation of every animal on `date`, all from packages/domain. */
export function useHerd(date?: string): HerdView {
  const animals = useAnimals();
  const events = useEvents();
  const settings = useSettings();
  const today = useToday();
  const d = date ?? today;
  return useMemo(() => {
    const eventsByAnimal = groupEventsByAnimal((events ?? []) as unknown as DomainEvent[]);
    const rows: AnimalRow[] = (animals ?? [])
      .map((a) => {
        const evs = eventsByAnimal.get(a.id) ?? [];
        const da = a as unknown as DomainAnimal;
        return {
          animal: a,
          stage: deriveStage(da, evs, d, settings),
          repro: deriveReproStatus(da, evs, d, settings),
          lact: currentLactation(da, evs, d),
          events: evs,
        };
      })
      .sort((x, y) => compareTags(x.animal, y.animal));
    return { rows, byId: new Map(rows.map((r) => [r.animal.id, r])), eventsByAnimal, loading: !animals || !events };
  }, [animals, events, settings, d]);
}

export function useUsers(): Map<string, PullUser> {
  const users = useLiveQuery(() => getMeta<PullUser[]>('users'), []);
  return useMemo(() => new Map((users ?? []).map((u) => [u.id, u])), [users]);
}

/** Last used values for form defaults (G-12): technician, buyer, payment method… */
export async function rememberLastUsed(key: string, value: string): Promise<void> {
  const all = ((await getMeta<Record<string, string>>('lastUsed')) ?? {}) as Record<string, string>;
  all[key] = value;
  await db.meta.put({ key: 'lastUsed', value: all });
}

export function useLastUsed(): Record<string, string> {
  return useLiveQuery(() => getMeta<Record<string, string>>('lastUsed'), []) ?? {};
}

export function useOutboxCounts(): { pending: number; rejected: number } {
  return (
    useLiveQuery(async () => ({
      pending: await db.outbox.where('status').equals('pending').count(),
      rejected: await db.outbox.where('status').equals('rejected').count(),
    })) ?? { pending: 0, rejected: 0 }
  );
}
